import { z } from 'zod';

/**
 * Wire contracts for `pending-postman-mock-server`.
 *
 * The repo's existing mock representation lives in
 * `api_mocks.routes JSONB`, one row per mock with an array of
 * `MockRoute` items inside. v1 of this feature pushes the
 * mode / template / sequence / proxy / share-link configuration
 * into those route objects (so reordering remains a pure JSONB
 * shuffle), while the share-link state lives at the mock level
 * (so the public dispatcher only needs one lookup).
 *
 * The `mockTemplateEngine` lives in this package as well
 * (`mock-template.ts`) so the in-product preview, the
 * dispatcher, and the offline cli can share the exact same
 * expansion behaviour.
 */

export const MOCK_ROUTE_MODES = ['fixed', 'sequence', 'proxy'] as const;
export type MockRouteMode = (typeof MOCK_ROUTE_MODES)[number];
export const MockRouteModeSchema = z.enum(MOCK_ROUTE_MODES);

export const MOCK_HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;
export type MockHttpMethod = (typeof MOCK_HTTP_METHODS)[number];
export const MockHttpMethodSchema = z.enum(MOCK_HTTP_METHODS);

/**
 * A single mocked endpoint inside the per-mock routes array.
 *
 * Backward-compatible with the v1 (fixed) shape: a row with
 * just `{ method, path, status, body, delay, headers }` keeps
 * working because the defaults below cover the new fields.
 */
export const MockRouteSchema = z.object({
  id: z.string(),
  method: MockHttpMethodSchema,
  path: z.string().min(1),
  status: z.number().int().min(100).max(599).default(200),
  headers: z.record(z.string()).default({}),
  delay: z.number().int().min(0).max(60_000).default(0),

  /** v1 default for legacy rows. */
  mode: MockRouteModeSchema.default('fixed'),

  /**
   * For `fixed` and `sequence` modes: a templated response
   * body. Sequence mode treats this as a JSON array.
   *
   * Empty string is allowed (a 204 No Content style mock).
   */
  body: z.string().default(''),

  /**
   * For `proxy` mode: the upstream URL to forward to. Subject
   * to `mockServer.proxyAllowlist` from admin config.
   */
  upstream_url: z.string().url().optional(),

  /**
   * For `proxy` mode: whether to record the response on hit
   * and replay it on subsequent unreachable-upstream calls.
   * Default `true` matches the design-doc "canary recording"
   * behaviour.
   */
  proxy_record: z.boolean().default(true),

  /**
   * Per-route override for the mock-level share-link rate
   * limit. NULL means "fall through to the mock-level limit".
   */
  rate_limit_per_min: z.number().int().min(1).max(60_000).nullable().default(null),
});
export type MockRoute = z.infer<typeof MockRouteSchema>;

export const MockSchema = z.object({
  id: z.string().uuid(),
  project_id: z.string().uuid(),
  collection_id: z.string().uuid().nullable(),
  name: z.string().min(1).max(120),
  routes: z.array(MockRouteSchema),
  port: z.number().int().min(1024).max(65535).default(9090),
  active: z.boolean().default(false),

  /** Set by `POST /api/mocks/:id/share-link` — when non-null
   *  the mock is reachable on the public dispatcher. */
  public_share_token: z.string().nullable().default(null),

  share_link_rate_limit_per_min: z.number().int().min(1).max(60_000).default(100),
  share_link_hit_count: z.number().int().nonnegative().default(0),
  share_link_last_hit_at: z.string().nullable().default(null),

  created_at: z.string(),
  updated_at: z.string(),
});
export type Mock = z.infer<typeof MockSchema>;

export const CreateMockRequestSchema = z.object({
  project_id: z.string().uuid(),
  collection_id: z.string().uuid().nullable().optional(),
  name: z.string().min(1).max(120),
  routes: z.array(MockRouteSchema.omit({ id: true }).extend({ id: z.string().optional() })).default([]),
  port: z.number().int().min(1024).max(65535).optional(),
});
export type CreateMockRequest = z.infer<typeof CreateMockRequestSchema>;

export const UpdateMockRequestSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  routes: z.array(MockRouteSchema.omit({ id: true }).extend({ id: z.string().optional() })).optional(),
  port: z.number().int().min(1024).max(65535).optional(),
  active: z.boolean().optional(),
  share_link_rate_limit_per_min: z.number().int().min(1).max(60_000).optional(),
});
export type UpdateMockRequest = z.infer<typeof UpdateMockRequestSchema>;

// =====================================================================
// Public share-link surface
// =====================================================================

export const ShareLinkResponseSchema = z.object({
  mock_id: z.string().uuid(),
  token: z.string().min(20),
  url: z.string().url(),
  created_at: z.string(),
});
export type ShareLinkResponse = z.infer<typeof ShareLinkResponseSchema>;

export const RotateShareLinkResponseSchema = z.object({
  mock_id: z.string().uuid(),
  previous_token: z.string().nullable(),
  token: z.string().min(20),
  url: z.string().url(),
  rotated_at: z.string(),
});
export type RotateShareLinkResponse = z.infer<typeof RotateShareLinkResponseSchema>;

// =====================================================================
// Live preview
// =====================================================================

export const PreviewTemplateRequestSchema = z.object({
  template: z.string().max(64_000),
  /** Sample request the author paste-and-tweaks in the UI. */
  sample_request: z
    .object({
      method: MockHttpMethodSchema.optional(),
      path: z.string().optional(),
      query: z.record(z.string()).optional(),
      headers: z.record(z.string()).optional(),
      body: z.unknown().optional(),
    })
    .default({}),
  /** Project env vars to expose as `{{env.<key>}}`. */
  env: z.record(z.string()).default({}),
});
export type PreviewTemplateRequest = z.infer<typeof PreviewTemplateRequestSchema>;

export const PreviewTemplateResponseSchema = z.object({
  resolved: z.string(),
  /** Variables the template referenced (for the editor hints). */
  variables_used: z.array(z.string()),
  /** Each missing variable — the editor surfaces these as warnings. */
  missing_variables: z.array(z.string()),
});
export type PreviewTemplateResponse = z.infer<typeof PreviewTemplateResponseSchema>;

// =====================================================================
// Hit-audit (light) — surfaces the recent share-link hits in
// the mock-edit drawer so the author can see adoption + spot
// abuse.
// =====================================================================

export const ShareLinkHitSchema = z.object({
  id: z.string().uuid(),
  mock_id: z.string().uuid(),
  route_method: MockHttpMethodSchema,
  route_path: z.string(),
  source_ip: z.string().nullable(),
  status_code: z.number().int(),
  duration_ms: z.number().int().nonnegative(),
  created_at: z.string(),
});
export type ShareLinkHit = z.infer<typeof ShareLinkHitSchema>;

export const ListShareLinkHitsResponseSchema = z.object({
  hits: z.array(ShareLinkHitSchema),
  total_count: z.number().int().nonnegative(),
});
export type ListShareLinkHitsResponse = z.infer<typeof ListShareLinkHitsResponseSchema>;
